import { List, Map, Set } from 'immutable';
import {
  IEntityFilters,
  ISearchParams,
  SEARCH_LIST_FILTER,
} from './entity-data.data';
import { Highlight } from '../search-data/sub-models/highlight.model';
import { EntityTypesEnum } from '../../app.enum';
import { IDispatchId } from '../../utils/helpers/redux.helper';
import { IFilteredLists } from './entity-data.reducer';

// action payload interfaces
export interface IEntityTypePayload {
  entityType: EntityTypesEnum;
}

export interface IEntityIdPayload {
  entityId: string;
}

export interface IEntityActionPayload {
  entity: any;
}

export interface IEntitiesActionPayload {
  entities: List<any>;
}

export interface IOriginalEntityPayload {
  originalEntity?: any;
}

export interface IPropertiesPayload {
  properties: Map<string, any>;
}

export interface IComparatorFn {
  comparatorFn: (entityA: any, entityB: any) => number;
}

export interface IFilterByFn {
  filterByFn: (entity: any) => boolean;
}

export interface IFetchedPayload {
  fetched: boolean;
}

export interface IErrorPayload {
  error: Error;
  data: any[];
}

/****
 LISTS
 ****/
export interface IListFilterPayload extends IEntityTypePayload {
  listFilter?: string;
}

export interface ISearchParamsPayload extends IListFilterPayload {
  searchParams: ISearchParams;
}

export interface IFetchListPayload extends ISearchParamsPayload {
  mergeEntities?: boolean;
}

export interface IPersistSearchParams extends IListFilterPayload {
  persistKey: string;
}

export interface ISearchListPayload extends ISearchParamsPayload {
  globalSearch: boolean;
  persistParams: boolean;
}

export interface IUpdateList
  extends ISearchParamsPayload,
    IComparatorFn,
    IFilterByFn {
  entities: List<any>;
  highlights: List<Highlight>;
}

export interface ISetAllLists extends IEntityTypePayload {
  filteredLists: IFilteredLists;
}

export interface ISetListFetchedPayload
  extends IListFilterPayload,
    IFetchedPayload {}

export interface IListErrorPayload extends IErrorPayload, IListFilterPayload {}

/*******
 FILTERS
 *******/
export interface IFetchFiltersPayload extends IListFilterPayload {
  delay: number;
}

export interface IClearFiltersPayload extends IListFilterPayload {}

export interface ISetFiltersPayload extends IListFilterPayload {
  filters: IEntityFilters;
}

export interface ISetFiltersFetchedPayload
  extends IListFilterPayload,
    IFetchedPayload {}

export interface IFetchFiltersErrorPayload
  extends IListFilterPayload,
    IErrorPayload {}

export interface ISetRefreshingFiltersPayload extends IListFilterPayload {
  refreshingFilters: boolean;
}

/******
 ENTITY
 ******/
export interface IEntityNetworkPayload
  extends IDispatchId,
    IEntityTypePayload,
    IEntityActionPayload {}

export interface IEntityNetworkResultPayload
  extends IDispatchId,
    IEntityTypePayload,
    IEntityActionPayload,
    IOriginalEntityPayload {
  sourceAction: string;
  error?: any[];
}

export interface IGetEntityById
  extends IDispatchId,
    IEntityTypePayload,
    IEntityIdPayload {
  useLocal: boolean;
}

export interface ISaveEntityStore
  extends IEntityTypePayload,
    IEntityActionPayload {
  useLocalId: boolean;
  listFilter?: string;
}

export interface ISaveEntityNetwork
  extends IDispatchId,
    ISaveEntityStore,
    IOriginalEntityPayload {
  sourceAction: any;
}

export interface IDeleteEntityStore
  extends IEntityTypePayload,
    IEntityActionPayload {
  useLocalId: boolean;
}

export interface ISetDispatchedResponse
  extends IEntityTypePayload,
    IDispatchId,
    IEntityActionPayload,
    IErrorPayload {
  action: string; // for debugging purpose
  isSuccess: boolean;
}

export interface ISaveNewEntity
  extends IDispatchId,
    IEntityTypePayload,
    IEntityActionPayload {}

export interface IUpdateEntity
  extends IDispatchId,
    IEntityTypePayload,
    IEntityIdPayload,
    IPropertiesPayload {}

export interface ISaveEditingEntity extends IDispatchId, IEntityTypePayload {}

export interface ICreateNewEditingEntity extends IEntityTypePayload {
  goBackUrl?: string;
}

export interface ISetEditingEntity
  extends IEntityTypePayload,
    IEntityActionPayload {
  setOriginalEntity?: boolean;
}

export interface ISetEditingEntityContent
  extends IDispatchId,
    IEntityTypePayload,
    IPropertiesPayload {
  saveEditing: boolean;
}

export interface ISetEditingEntityProperties
  extends IEntityTypePayload,
    IPropertiesPayload {}

export interface ISetEditingEntityField extends IEntityTypePayload {
  key: string | string[];
  value: any;
}

export interface IResetEditingEntity extends IEntityTypePayload {}

export interface IRefreshEditingEntity extends IEntityTypePayload {
  saveEntityToStore: boolean;
}

export interface IEntitySelectedList
  extends IEntityTypePayload,
    IEntityActionPayload {}

export interface IEntityLimitedSelectedList extends IEntitySelectedList {
  limit: number;
}

export interface IResetSelectedList extends IEntityTypePayload {}

export interface ISetSelectedList extends IEntityTypePayload {
  entities: Set<any>;
}

export interface IDeleteSelectedEntities
  extends IDispatchId,
    IEntityTypePayload,
    IListFilterPayload {}

export interface IBatchDeleteEntityStore
  extends IEntityTypePayload,
    IEntitiesActionPayload {}

export interface IBatchSaveEntityStore
  extends IBatchDeleteEntityStore,
    IListFilterPayload {}

export interface IBatchDeleteEntityNetworkResponse
  extends IDispatchId,
    IEntityTypePayload,
    IEntitiesActionPayload {
  sourceAction: string;
}

export interface IBatchDeleteEntityNetwork
  extends IBatchDeleteEntityNetworkResponse,
    IListFilterPayload {}

/** -------------------- **/

// action enums
export enum ActionsEnum {
  FETCH_LIST = 'FETCH_LIST',
  FETCH_NEXT_PAGE = 'FETCH_NEXT_PAGE',
  CLEAR_LIST = 'CLEAR_LIST',
  SEARCH_LIST = 'SEARCH_LIST',
  UPDATE_LIST = 'UPDATE_LIST',
  SET_ALL_LISTS = 'SET_ALL_LISTS',
  SET_LIST_FETCHED = 'SET_LIST_FETCHED',
  FETCH_LIST_ERROR = 'FETCH_LIST_ERROR',
  SET_SEARCH_PARAMS = 'SET_SEARCH_PARAMS',
  PERSIST_SEARCH_PARAMS = 'PERSIST_SEARCH_PARAMS',
  FETCH_FILTERS = 'FETCH_FILTERS',
  CLEAR_FILTERS = 'CLEAR_FILTERS',
  SET_FILTERS = 'SET_FILTERS',
  SET_FILTERS_FETCHED = 'SET_FILTERS_FETCHED',
  SET_REFRESHING_FILTERS = 'SET_REFRESHING_FILTERS',
  FETCH_FILTERS_ERROR = 'FETCH_FILTERS_ERROR',
  GET_ENTITY_BY_ID = 'GET_ENTITY_BY_ID',
  SAVE_NEW_ENTITY = 'SAVE_NEW_ENTITY',
  UPDATE_ENTITY = 'UPDATE_ENTITY',
  SAVE_EDITING_ENTITY = 'SAVE_EDITING_ENTITY',
  SAVE_ENTITY_STORE = 'SAVE_ENTITY_STORE',
  DELETE_ENTITY_STORE = 'DELETE_ENTITY_STORE',
  SAVE_ENTITY_NETWORK = 'SAVE_ENTITY_NETWORK',
  SAVE_ENTITY_NETWORK_SUCCESS = 'SAVE_ENTITY_NETWORK_SUCCESS',
  SAVE_ENTITY_NETWORK_ERROR = 'SAVE_ENTITY_NETWORK_ERROR',
  DELETE_ENTITY_NETWORK = 'DELETE_ENTITY_NETWORK',
  DELETE_ENTITY_NETWORK_SUCCESS = 'DELETE_ENTITY_NETWORK_SUCCESS',
  DELETE_ENTITY_NETWORK_ERROR = 'DELETE_ENTITY_NETWORK_ERROR',
  CREATE_NEW_EDITING_ENTITY = 'CREATE_NEW_EDITING_ENTITY',
  SET_EDITING_ENTITY = 'SET_EDITING_ENTITY',
  SET_EDITING_ENTITY_CONTENT = 'SET_EDITING_ENTITY_CONTENT',
  SET_EDITING_ENTITY_PROPERTIES = 'SET_EDITING_ENTITY_PROPERTIES',
  SET_EDITING_ENTITY_FIELD = 'SET_EDITING_ENTITY_FIELD',
  REFRESH_EDITING_ENTITY = 'REFRESH_EDITING_ENTITY',
  RESET_EDITING_ENTITY = 'RESET_EDITING_ENTITY',
  ADD_TO_LIMITED_SELECTED_LIST = 'ADD_TO_LIMITED_SELECTED_LIST',
  ADD_TO_SELECTED_LIST = 'ADD_TO_SELECTED_LIST',
  REMOVE_FROM_SELECTED_LIST = 'REMOVE_FROM_SELECTED_LIST',
  RESET_SELECTED_LIST = 'RESET_SELECTED_LIST',
  SET_SELECTED_LIST = 'SET_SELECTED_LIST',
  SET_DISPATCHED_RESPONSE = 'SET_DISPATCHED_RESPONSE',
  SET_MASTER_ERROR = 'SET_MASTER_ERROR',
  CLEAR_MASTER_ERROR = 'CLEAR_MASTER_ERROR',
  DELETE_SELECTED_ENTITIES = 'DELETE_SELECTED_ENTITIES',
  BATCH_SAVE_ENTITY_STORE = 'BATCH_SAVE_ENTITY_STORE',
  BATCH_DELETE_ENTITY_STORE = 'BATCH_DELETE_ENTITY_STORE',
  BATCH_DELETE_ENTITY_NETWORK = 'BATCH_DELETE_ENTITY_NETWORK',
  BATCH_DELETE_ENTITY_NETWORK_SUCCESS = 'BATCH_DELETE_ENTITY_NETWORK_SUCCESS',
  BATCH_DELETE_ENTITY_NETWORK_ERROR = 'BATCH_DELETE_ENTITY_NETWORK_ERROR',
}

// action definitions
export interface FetchList {
  type: ActionsEnum.FETCH_LIST;
  payload: IFetchListPayload;
}

export interface FetchNextPage {
  type: ActionsEnum.FETCH_NEXT_PAGE;
  payload: IListFilterPayload;
}

export interface ClearList {
  type: ActionsEnum.CLEAR_LIST;
  payload: IListFilterPayload;
}

export interface SearchList {
  type: ActionsEnum.SEARCH_LIST;
  payload: ISearchListPayload;
}

export interface UpdateList {
  type: ActionsEnum.UPDATE_LIST;
  payload: IUpdateList;
}

export interface SetAllLists {
  type: ActionsEnum.SET_ALL_LISTS;
  payload: ISetAllLists;
}

export interface SetListFetched {
  type: ActionsEnum.SET_LIST_FETCHED;
  payload: ISetListFetchedPayload;
}

export interface FetchListError {
  type: ActionsEnum.FETCH_LIST_ERROR;
  payload: IListErrorPayload;
}

export interface SetSearchParams {
  type: ActionsEnum.SET_SEARCH_PARAMS;
  payload: ISearchParamsPayload;
}

export interface PersistSearchParams {
  type: ActionsEnum.PERSIST_SEARCH_PARAMS;
  payload: IPersistSearchParams;
}

export interface FetchFilters {
  type: ActionsEnum.FETCH_FILTERS;
  payload: IFetchFiltersPayload;
}

export interface ClearFilters {
  type: ActionsEnum.CLEAR_FILTERS;
  payload: IClearFiltersPayload;
}

export interface SetFilters {
  type: ActionsEnum.SET_FILTERS;
  payload: ISetFiltersPayload;
}

export interface SetFiltersFetched {
  type: ActionsEnum.SET_FILTERS_FETCHED;
  payload: ISetFiltersFetchedPayload;
}

export interface FetchFiltersError {
  type: ActionsEnum.FETCH_FILTERS_ERROR;
  payload: IFetchFiltersErrorPayload;
}

export interface SetRefreshingFilters {
  type: ActionsEnum.SET_REFRESHING_FILTERS;
  payload: ISetRefreshingFiltersPayload;
}

export interface GetEntityById {
  type: ActionsEnum.GET_ENTITY_BY_ID;
  payload: IGetEntityById;
}

export interface SaveNewEntity {
  type: ActionsEnum.SAVE_NEW_ENTITY;
  payload: ISaveNewEntity;
}

export interface UpdateEntity {
  type: ActionsEnum.UPDATE_ENTITY;
  payload: IUpdateEntity;
}

export interface SaveEditingEntity {
  type: ActionsEnum.SAVE_EDITING_ENTITY;
  payload: ISaveEditingEntity;
}

export interface SaveEntityStore {
  type: ActionsEnum.SAVE_ENTITY_STORE;
  payload: ISaveEntityStore;
}

export interface DeleteEntityStore {
  type: ActionsEnum.DELETE_ENTITY_STORE;
  payload: IDeleteEntityStore;
}

export interface SaveEntityNetwork {
  type: ActionsEnum.SAVE_ENTITY_NETWORK;
  payload: ISaveEntityNetwork;
}

export interface SaveEntityNetworkSuccess {
  type: ActionsEnum.SAVE_ENTITY_NETWORK_SUCCESS;
  payload: IEntityNetworkResultPayload;
}

export interface SaveEntityNetworkError {
  type: ActionsEnum.SAVE_ENTITY_NETWORK_ERROR;
  payload: IEntityNetworkResultPayload;
}

export interface DeleteEntityNetwork {
  type: ActionsEnum.DELETE_ENTITY_NETWORK;
  payload: IEntityNetworkPayload;
}

export interface DeleteEntityNetworkSuccess {
  type: ActionsEnum.DELETE_ENTITY_NETWORK_SUCCESS;
  payload: IEntityNetworkResultPayload;
}

export interface DeleteEntityNetworkError {
  type: ActionsEnum.DELETE_ENTITY_NETWORK_ERROR;
  payload: IEntityNetworkResultPayload;
}

export interface CreateNewEditingEntity {
  type: ActionsEnum.CREATE_NEW_EDITING_ENTITY;
  payload: ICreateNewEditingEntity;
}

export interface SetEditingEntity {
  type: ActionsEnum.SET_EDITING_ENTITY;
  payload: ISetEditingEntity;
}

export interface SetEditingEntityContent {
  type: ActionsEnum.SET_EDITING_ENTITY_CONTENT;
  payload: ISetEditingEntityContent;
}

export interface SetEditingEntityProperties {
  type: ActionsEnum.SET_EDITING_ENTITY_PROPERTIES;
  payload: ISetEditingEntityProperties;
}

export interface SetEditingEntityField {
  type: ActionsEnum.SET_EDITING_ENTITY_FIELD;
  payload: ISetEditingEntityField;
}

export interface ResetEditingEntity {
  type: ActionsEnum.RESET_EDITING_ENTITY;
  payload: IResetEditingEntity;
}

export interface RefreshEditingEntity {
  type: ActionsEnum.REFRESH_EDITING_ENTITY;
  payload: IRefreshEditingEntity;
}

export interface AddToLimitedSelectedList {
  type: ActionsEnum.ADD_TO_LIMITED_SELECTED_LIST;
  payload: IEntityLimitedSelectedList;
}

export interface AddToSelectedList {
  type: ActionsEnum.ADD_TO_SELECTED_LIST;
  payload: IEntitySelectedList;
}

export interface RemoveFromSelectedList {
  type: ActionsEnum.REMOVE_FROM_SELECTED_LIST;
  payload: IEntitySelectedList;
}

export interface ResetSelectedList {
  type: ActionsEnum.RESET_SELECTED_LIST;
  payload: IResetSelectedList;
}

export interface SetSelectedList {
  type: ActionsEnum.SET_SELECTED_LIST;
  payload: ISetSelectedList;
}

export interface SetDispatchedResponse {
  type: ActionsEnum.SET_DISPATCHED_RESPONSE;
  payload: ISetDispatchedResponse;
}

export interface SetMasterError {
  type: ActionsEnum.SET_MASTER_ERROR;
  payload: IErrorPayload;
}

export interface ClearMasterError {
  type: ActionsEnum.CLEAR_MASTER_ERROR;
}

export interface DeleteSelectedEntities {
  type: ActionsEnum.DELETE_SELECTED_ENTITIES;
  payload: IDeleteSelectedEntities;
}

export interface BatchSaveEntityStore {
  type: ActionsEnum.BATCH_SAVE_ENTITY_STORE;
  payload: IBatchSaveEntityStore;
}

export interface BatchDeleteEntityStore {
  type: ActionsEnum.BATCH_DELETE_ENTITY_STORE;
  payload: IBatchDeleteEntityStore;
}

export interface BatchDeleteEntityNetwork {
  type: ActionsEnum.BATCH_DELETE_ENTITY_NETWORK;
  payload: IBatchDeleteEntityNetwork;
}

export interface BatchDeleteEntityNetworkSuccess {
  type: ActionsEnum.BATCH_DELETE_ENTITY_NETWORK_SUCCESS;
  payload: IBatchDeleteEntityNetworkResponse;
}

export interface BatchDeleteEntityNetworkError {
  type: ActionsEnum.BATCH_DELETE_ENTITY_NETWORK_ERROR;
  payload: IBatchDeleteEntityNetworkResponse;
}

// action creators
export const fetchList = (
  entityType: EntityTypesEnum,
  searchParams: ISearchParams = {},
  listFilter?: string,
  mergeEntities?: boolean
): FetchList => ({
  type: ActionsEnum.FETCH_LIST,
  payload: {
    entityType,
    searchParams,
    listFilter,
    mergeEntities,
  },
});

export const fetchNextPage = (
  entityType: EntityTypesEnum,
  listFilter?: string
): FetchNextPage => ({
  type: ActionsEnum.FETCH_NEXT_PAGE,
  payload: {
    entityType,
    listFilter,
  },
});

export const clearList = (
  entityType: EntityTypesEnum,
  listFilter?: string
): ClearList => ({
  type: ActionsEnum.CLEAR_LIST,
  payload: {
    entityType,
    listFilter,
  },
});

export const searchList = (
  entityType: EntityTypesEnum,
  searchParams: ISearchParams = {},
  globalSearch: boolean = false,
  persistParams: boolean = true,
  listFilter: string = SEARCH_LIST_FILTER
): SearchList => ({
  type: ActionsEnum.SEARCH_LIST,
  payload: {
    entityType,
    searchParams,
    globalSearch,
    persistParams,
    listFilter,
  },
});

export const updateList = (
  entityType: EntityTypesEnum,
  entities: List<any>,
  searchParams: ISearchParams,
  comparatorFn: (itemA: any, itemB: any) => number,
  filterByFn: (item: any) => boolean,
  highlights?: List<Highlight>,
  listFilter?: string
): UpdateList => ({
  type: ActionsEnum.UPDATE_LIST,
  payload: {
    entityType,
    entities,
    searchParams,
    comparatorFn,
    filterByFn,
    highlights,
    listFilter,
  },
});

export const setAllLists = (
  entityType: EntityTypesEnum,
  filteredLists: IFilteredLists
): SetAllLists => ({
  type: ActionsEnum.SET_ALL_LISTS,
  payload: {
    entityType,
    filteredLists,
  },
});

export const setListFetched = (
  entityType: EntityTypesEnum,
  fetched: boolean,
  listFilter?: string
): SetListFetched => ({
  type: ActionsEnum.SET_LIST_FETCHED,
  payload: {
    entityType,
    fetched,
    listFilter,
  },
});

export const fetchListError = (
  entityType: EntityTypesEnum,
  error: Error,
  data: any[],
  listFilter: string
): FetchListError => ({
  type: ActionsEnum.FETCH_LIST_ERROR,
  payload: {
    entityType,
    error,
    data,
    listFilter,
  },
});

export const setSearchParams = (
  entityType: EntityTypesEnum,
  searchParams: ISearchParams,
  listFilter: string
): SetSearchParams => ({
  type: ActionsEnum.SET_SEARCH_PARAMS,
  payload: {
    entityType,
    searchParams,
    listFilter,
  },
});

export const persistSearchParams = (
  entityType: EntityTypesEnum,
  listFilter: string,
  persistKey: string
): PersistSearchParams => ({
  type: ActionsEnum.PERSIST_SEARCH_PARAMS,
  payload: {
    entityType,
    listFilter,
    persistKey,
  },
});

export const fetchFilters = (
  entityType: EntityTypesEnum,
  delay: number,
  listFilter?: string
): FetchFilters => ({
  type: ActionsEnum.FETCH_FILTERS,
  payload: {
    entityType,
    delay,
    listFilter,
  },
});

export const clearFilters = (
  entityType: EntityTypesEnum,
  listFilter?: string
): ClearFilters => ({
  type: ActionsEnum.CLEAR_FILTERS,
  payload: {
    entityType,
    listFilter,
  },
});

export const setFilters = (
  entityType: EntityTypesEnum,
  filters: IEntityFilters,
  listFilter?: string
): SetFilters => ({
  type: ActionsEnum.SET_FILTERS,
  payload: {
    entityType,
    filters,
    listFilter,
  },
});

export const setFiltersFetched = (
  entityType: EntityTypesEnum,
  fetched: boolean,
  listFilter?: string
): SetFiltersFetched => ({
  type: ActionsEnum.SET_FILTERS_FETCHED,
  payload: {
    entityType,
    fetched,
    listFilter,
  },
});

export const setRefreshingFilters = (
  entityType: EntityTypesEnum,
  refreshingFilters: boolean,
  listFilter?: string
): SetRefreshingFilters => ({
  type: ActionsEnum.SET_REFRESHING_FILTERS,
  payload: {
    entityType,
    refreshingFilters,
    listFilter,
  },
});

export const fetchFiltersError = (
  entityType: EntityTypesEnum,
  error: Error,
  data: any[],
  listFilter?: string
): FetchFiltersError => ({
  type: ActionsEnum.FETCH_FILTERS_ERROR,
  payload: {
    entityType,
    error,
    data,
    listFilter,
  },
});

export const getEntityById = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  entityId: string,
  useLocal: boolean
): GetEntityById => ({
  type: ActionsEnum.GET_ENTITY_BY_ID,
  payload: {
    dispatchId,
    entityType,
    entityId,
    useLocal,
  },
});

export const saveNewEntity = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  entity: any
): SaveNewEntity => ({
  type: ActionsEnum.SAVE_NEW_ENTITY,
  payload: {
    dispatchId,
    entityType,
    entity,
  },
});

export const updateEntity = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  entityId: string,
  properties: Map<string, any>
): UpdateEntity => ({
  type: ActionsEnum.UPDATE_ENTITY,
  payload: {
    dispatchId,
    entityType,
    entityId,
    properties,
  },
});

export const saveEditingEntity = (
  dispatchId: string,
  entityType: EntityTypesEnum
): SaveEditingEntity => ({
  type: ActionsEnum.SAVE_EDITING_ENTITY,
  payload: {
    dispatchId,
    entityType,
  },
});

export const saveEntityStore = (
  entityType: EntityTypesEnum,
  entity: any,
  useLocalId: boolean,
  listFilter?: string
): SaveEntityStore => ({
  type: ActionsEnum.SAVE_ENTITY_STORE,
  payload: {
    entityType,
    entity,
    useLocalId,
    listFilter,
  },
});

export const saveEntityNetwork = (
  dispatchId: string,
  sourceAction: string,
  entityType: EntityTypesEnum,
  entity: any,
  useLocalId: boolean,
  originalEntity?: any
): SaveEntityNetwork => ({
  type: ActionsEnum.SAVE_ENTITY_NETWORK,
  payload: {
    dispatchId,
    sourceAction,
    entityType,
    entity,
    useLocalId,
    originalEntity,
  },
});

export const saveEntityNetworkSuccess = (
  dispatchId: string,
  sourceAction: string,
  entityType: EntityTypesEnum,
  entity: any
): SaveEntityNetworkSuccess => ({
  type: ActionsEnum.SAVE_ENTITY_NETWORK_SUCCESS,
  payload: {
    dispatchId,
    sourceAction,
    entityType,
    entity,
  },
});

export const saveEntityNetworkError = (
  dispatchId: string,
  sourceAction: string,
  entityType: EntityTypesEnum,
  entity: any,
  originalEntity: any,
  error: any[]
): SaveEntityNetworkError => ({
  type: ActionsEnum.SAVE_ENTITY_NETWORK_ERROR,
  payload: {
    dispatchId,
    sourceAction,
    entityType,
    entity,
    originalEntity,
    error,
  },
});

export const deleteEntityStore = (
  entityType: EntityTypesEnum,
  entity: any,
  useLocalId: boolean
): DeleteEntityStore => ({
  type: ActionsEnum.DELETE_ENTITY_STORE,
  payload: {
    entityType,
    entity,
    useLocalId,
  },
});

export const deleteEntityNetwork = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  entity: any
): DeleteEntityNetwork => ({
  type: ActionsEnum.DELETE_ENTITY_NETWORK,
  payload: {
    dispatchId,
    entityType,
    entity,
  },
});

export const deleteEntityNetworkSuccess = (
  dispatchId: string,
  sourceAction: string,
  entityType: EntityTypesEnum,
  entity: any
): DeleteEntityNetworkSuccess => ({
  type: ActionsEnum.DELETE_ENTITY_NETWORK_SUCCESS,
  payload: {
    dispatchId,
    sourceAction,
    entityType,
    entity,
  },
});

export const deleteEntityNetworkError = (
  dispatchId: string,
  sourceAction: string,
  entityType: EntityTypesEnum,
  entity: any
): DeleteEntityNetworkError => ({
  type: ActionsEnum.DELETE_ENTITY_NETWORK_ERROR,
  payload: {
    dispatchId,
    sourceAction,
    entityType,
    entity,
  },
});

export const createNewEditingEntity = (
  entityType: EntityTypesEnum,
  goBackUrl?: string
): CreateNewEditingEntity => ({
  type: ActionsEnum.CREATE_NEW_EDITING_ENTITY,
  payload: {
    entityType,
    goBackUrl,
  },
});

export const setEditingEntity = (
  entityType: EntityTypesEnum,
  entity: any,
  setOriginalEntity?: boolean
): SetEditingEntity => ({
  type: ActionsEnum.SET_EDITING_ENTITY,
  payload: {
    entityType,
    entity,
    setOriginalEntity,
  },
});

export const setEditingEntityContent = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  properties: Map<string, any>,
  saveEditing: boolean
): SetEditingEntityContent => ({
  type: ActionsEnum.SET_EDITING_ENTITY_CONTENT,
  payload: {
    dispatchId,
    entityType,
    properties,
    saveEditing,
  },
});

export const setEditingEntityProperties = (
  entityType: EntityTypesEnum,
  properties: Map<string, any>
): SetEditingEntityProperties => ({
  type: ActionsEnum.SET_EDITING_ENTITY_PROPERTIES,
  payload: {
    entityType,
    properties,
  },
});

export const setEditingEntityField = (
  entityType: EntityTypesEnum,
  key: string | string[],
  value: any
): SetEditingEntityField => ({
  type: ActionsEnum.SET_EDITING_ENTITY_FIELD,
  payload: {
    entityType,
    key,
    value,
  },
});

export const resetEditingEntity = (
  entityType: EntityTypesEnum
): ResetEditingEntity => ({
  type: ActionsEnum.RESET_EDITING_ENTITY,
  payload: {
    entityType,
  },
});

export const refreshEditingEntity = (
  entityType: EntityTypesEnum,
  saveEntityToStore: boolean
): RefreshEditingEntity => ({
  type: ActionsEnum.REFRESH_EDITING_ENTITY,
  payload: {
    entityType,
    saveEntityToStore,
  },
});

export const addToLimitedSelectedList = (
  entityType: EntityTypesEnum,
  entity: any,
  limit: number
): AddToLimitedSelectedList => ({
  type: ActionsEnum.ADD_TO_LIMITED_SELECTED_LIST,
  payload: {
    entityType,
    entity,
    limit,
  },
});

export const addToSelectedList = (
  entityType: EntityTypesEnum,
  entity: any
): AddToSelectedList => ({
  type: ActionsEnum.ADD_TO_SELECTED_LIST,
  payload: {
    entityType,
    entity,
  },
});

export const removeFromSelectedList = (
  entityType: EntityTypesEnum,
  entity: any
): RemoveFromSelectedList => ({
  type: ActionsEnum.REMOVE_FROM_SELECTED_LIST,
  payload: {
    entityType,
    entity,
  },
});

export const resetSelectedList = (
  entityType: EntityTypesEnum
): ResetSelectedList => ({
  type: ActionsEnum.RESET_SELECTED_LIST,
  payload: {
    entityType,
  },
});

export const setSelectedList = (
  entityType: EntityTypesEnum,
  entities: Set<any>
): SetSelectedList => ({
  type: ActionsEnum.SET_SELECTED_LIST,
  payload: {
    entityType,
    entities,
  },
});

export const setDispatchedResponse = (
  entityType: EntityTypesEnum,
  dispatchId: string,
  action: string,
  isSuccess: boolean,
  entity: any,
  error?: Error,
  data?: any[]
): SetDispatchedResponse => ({
  type: ActionsEnum.SET_DISPATCHED_RESPONSE,
  payload: {
    entityType,
    dispatchId,
    action,
    isSuccess,
    entity,
    error,
    data,
  },
});

export const setMasterError = (error: Error, data?: any[]): SetMasterError => ({
  type: ActionsEnum.SET_MASTER_ERROR,
  payload: {
    error,
    data,
  },
});

export const clearMasterError = (): ClearMasterError => ({
  type: ActionsEnum.CLEAR_MASTER_ERROR,
});

export const deleteSelectedEntities = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  listFilter?: string
): DeleteSelectedEntities => ({
  type: ActionsEnum.DELETE_SELECTED_ENTITIES,
  payload: {
    dispatchId,
    entityType,
    listFilter,
  },
});

export const batchSaveEntityStore = (
  entityType: EntityTypesEnum,
  entities: List<any>,
  listFilter?: string
): BatchSaveEntityStore => ({
  type: ActionsEnum.BATCH_SAVE_ENTITY_STORE,
  payload: {
    entityType,
    entities,
    listFilter,
  },
});

export const batchDeleteEntityStore = (
  entityType: EntityTypesEnum,
  entities: List<any>
): BatchDeleteEntityStore => ({
  type: ActionsEnum.BATCH_DELETE_ENTITY_STORE,
  payload: {
    entityType,
    entities,
  },
});

export const batchDeleteEntityNetwork = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  entities: List<any>,
  sourceAction: string,
  listFilter?: string
): BatchDeleteEntityNetwork => ({
  type: ActionsEnum.BATCH_DELETE_ENTITY_NETWORK,
  payload: {
    dispatchId,
    entityType,
    entities,
    sourceAction,
    listFilter,
  },
});

export const batchDeleteEntityNetworkSuccess = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  entities: List<any>,
  sourceAction: string
): BatchDeleteEntityNetworkSuccess => ({
  type: ActionsEnum.BATCH_DELETE_ENTITY_NETWORK_SUCCESS,
  payload: {
    dispatchId,
    entityType,
    entities,
    sourceAction,
  },
});

export const batchDeleteEntityNetworkError = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  entities: List<any>,
  sourceAction: string
): BatchDeleteEntityNetworkError => ({
  type: ActionsEnum.BATCH_DELETE_ENTITY_NETWORK_ERROR,
  payload: {
    dispatchId,
    entityType,
    entities,
    sourceAction,
  },
});
